import { bootstrap } from '../util';
import L from 'leaflet';
import 'Leaflet.MultiOptionsPolyline';
import Marker from '../../assets/map/Marker';

// First element is fill color, second is border color
const speedColors = [
    ['#00b4fe', '#0f93de'],
    ['#1a77bc', '#1b50a2'],
    ['#8f278e', '#751987'],
    ['#ee4339', '#d83536'],
    ['#B81200', '#851300']
];

function getSpeedColor(speed) {
    let speedThresholds = [30, 50, 90, 130];

    for (let i = 0; i < speedThresholds.length; ++i) {
        if (speed <= speedThresholds[i]) {
            return speedColors[i];
        }
    }
    return speedColors[speedThresholds.length];
}

function setBoundsFromGeographicalPoints(map, geoObjects) {
    let bounds = null;

    for (let location of geoObjects) {
        let lat = location.latLng[0];
        let lng = location.latLng[1];

        if (!lat || !lng) {
            continue;
        }

        if (!bounds) {
            bounds = [
                [lat, lng],
                [lat, lng]
            ];
        } else {
            bounds[0][0] = Math.min(bounds[0][0], lat);
            bounds[0][1] = Math.min(bounds[0][1], lng);
            bounds[1][0] = Math.max(bounds[1][0], lat);
            bounds[1][1] = Math.max(bounds[1][1], lng);
        }
    }

    if (bounds) {
        map.fitBounds(bounds);
    }
}

const moduleMap = {
    namespaced: true,
    state: {
        map: null,
        showLabels: true,
        showPoi: false,
        showMe: false,
        showIsolated: true,
        reloadMarkersWatcher: 0, // This variable is only used as a watcher to reload markers on the map.
        followDevice: -1,
        selectedProvider: 0,
        layerGroups: {
            'markers': L.layerGroup(),
            'me': L.layerGroup(),
            'history': L.layerGroup(),
            'historyZoom': L.layerGroup()
        },
        historySessions: [],
        historyEndDate: new Date(),
        tileProviders: [
            {
                name: 'OpenStreetMap', // BB cache
                visible: true,
                attribution: '&copy; Blackblox 2019 | &copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                url: 'https://maps.blackblox.si/{z}/{x}/{y}.png'
            },
            {
                name: 'OpenTopoMap',
                visible: false,
                url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
                attribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
            }
        ]
    },
    mutations: {
        tileProviders(state, idx) {
            for (let [t, tile] of state.tileProviders.entries()) {
                tile.visible = (t === idx);
            }
            state.selectedProvider = idx;
        },
        map(state, map) {
            state.map = map;
        },
        historySessions(state, historySessions) {
            state.historySessions = historySessions;
        },
        reloadMarkers(state) {
            state.reloadMarkersWatcher += 1;
        },
        historyEndDate(state, value) {
            state.historyEndDate = value;
        }
    },
    actions: {
        map(context, map) {
            // Init map and layer groups
            context.commit('map', map);

            for (let layerGroup of Object.values(context.state.layerGroups)) {
                layerGroup.addTo(map);
            }

            // Show/hide zoomed markers when zoom level is higher than 13
            map.on('zoomend', function() {
                context.dispatch('toggleHistoryMarkersOnZoom');
            });
        },
        invalidateMapSize(context) {
            // This is a hack to resize map, when window panel is resized or hidden.
            setTimeout(function() {
                context.state.map.invalidateSize();
            }, 200);
        },
        toggleHistoryMarkersOnZoom(context) {
            if (context.state.map.getZoom() > 13) {
                context.dispatch('showLayerGroup', 'historyZoom');
            } else {
                context.dispatch('hideLayerGroup', 'historyZoom');
            }
        },
        clearLayerGroupLayers(context, name) {
            context.state.layerGroups[name].clearLayers();
        },
        addLayerGroup(context, name) {
            // Show layer group if it is not already present
            if (!context.state.map.hasLayer(context.state.layerGroups[name])) {
                context.state.map.addLayer(context.state.layerGroups[name]);
            }
        },
        removeLayerGroup(context, name) {
            // Hide layer group if it is present
            if (context.state.map.hasLayer(context.state.layerGroups[name])) {
                context.state.map.removeLayer(context.state.layerGroups[name]);
            }
        },
        showLayerGroup(context, name) {
            // Change layer group opacity to 1
            context.state.layerGroups[name].eachLayer(function(layer) {
                layer.setStyle({ opacity: 1, fillOpacity: 1 });
            });
        },
        hideLayerGroup(context, name) {
            // Change layer group opacity to 0
            context.state.layerGroups[name].eachLayer(function(layer) {
                layer.setStyle({ opacity: 0, fillOpacity: 0 });
            });
        },
        addLayerToGroup(context, [ groupName, layer ]) {
            context.state.layerGroups[groupName].addLayer(layer);
        },
        addMarker(context, [ groupName, toPoint, markerOptions ]) {
            let marker = new Marker(toPoint, markerOptions);
            context.dispatch('addLayerToGroup', [ groupName, marker ]);
        },
        clearHistorySessions(context) {
            context.commit('historySessions', []);
            context.dispatch('clearLayerGroupLayers', 'history');
            context.dispatch('clearLayerGroupLayers', 'historyZoom');
        },
        setHistorySessions(context, [sessions, columns, historyStart, historyEnd]) {
            // Code reference in HistoryService.coffee renderHistoryForDevice()
            context.commit('historySessions', sessions);

            let device = context.state.followDevice;

            // Calculate column offsets, because data comes as 1D array.
            // Note: this could already be done on the backend as it would be cleaner and faster!
            let latColOffset = columns.indexOf('lat');
            let lonColOffset = columns.indexOf('lon');
            let timeColOffset = columns.indexOf('time');
            let speedColOffset = columns.indexOf('speed');
            let dfzColOffset = columns.indexOf('distance');
            let altColOffset = columns.indexOf('alt');

            // Sort sessions
            sessions.sort((a, b) => context.rootState.moment(a.from.time).diff(context.rootState.moment(b.from.time)));

            let locations = [];
            let locationsPerDay = {};
            let split = true;

            let lastRide = `R#${sessions[0].id}`;
            let previousLocations = [];

            for (let session of sessions) {
                let sessionLocations = [];
                let sessionStartTime = context.rootState.moment(session.from.time);

                if (session.type === 'PRIVATE_NORMAL') {
                    split = true;
                }

                for (let i = 0; i < session.locations.length; i += columns.length) {
                    // Parse session location and store data to the point variable.
                    let location = {
                        id: `${session.id}_${i}`,
                        latLng: [session.locations[i + latColOffset], session.locations[i + lonColOffset]],
                        time: sessionStartTime
                            .clone()
                            .add(session.locations[i + timeColOffset], 'seconds'),
                        speed: session.locations[i + speedColOffset],
                        dfz: dfzColOffset !== -1 ? session.locations[i + dfzColOffset] : undefined,
                        alt: altColOffset !== -1 ? session.locations[i + altColOffset] : undefined
                    };

                    // Filter out locations which extend outside the time period
                    // because they belong to the same vehicle session
                    if (!location.time.isBetween(historyStart, historyEnd)) {
                        continue;
                    }

                    sessionLocations.push(location);
                }

                // sessionLocations = sessionLocations.filter((loc) => loc.time.isBetween(historyStart, historyEnd));

                if (sessionLocations.length > 0 && session.type !== 'PRIVATE_NORMAL') {
                    if (split) {
                        lastRide = `R#${session.id}`;
                        locationsPerDay[lastRide] = sessionLocations;
                        split = false;
                        previousLocations = [];
                    } else {
                        previousLocations = locationsPerDay[lastRide];
                        previousLocations.push.apply(previousLocations, sessionLocations);
                    }

                    locations.push.apply(locations, sessionLocations);
                }
            }

            // Disable follow device
            context.commit('followDevice', -1);

            // Center map on the history path
            setBoundsFromGeographicalPoints(context.state.map, locations);

            for (let [ride, dailyLocations] of Object.entries(locationsPerDay)) {
                let lineOptions = [];
                let latLngs = [];

                if (dailyLocations.length <= 0) {
                    continue;
                }

                // Add a start marker
                let start = dailyLocations[0];
                context.dispatch('addMarker', [
                    'history',
                    start,
                    { type: 'start' }
                ]);

                // Marker types: start, stop, lastEnd
                let markerType;
                let endLocation = dailyLocations[dailyLocations.length - 1];
                if (ride === lastRide) {
                    markerType = 'lastEnd';
                    endLocation.device = {
                        label: device.label,
                        group: device.group
                    };
                } else {
                    markerType = 'end';
                }

                context.dispatch('addMarker', [
                    'history',
                    endLocation,
                    { type: markerType }
                ]);

                for (let i = 0; i < dailyLocations.length; i++) {
                    let location = dailyLocations[i];

                    // Get line color based on the speed of the section.
                    let color = getSpeedColor(location.speed);

                    // First point does not finish a line segment so skip it (comment copied from the old version)
                    if (i > 0) {
                        lineOptions.push({
                            weight: 8,
                            lineCap: 'round',
                            lineJoin: 'round',
                            smoothFactor: 1,
                            color: color[0]
                        });

                        latLngs.push(location.latLng);
                    }

                    // Add time and other info to location in the popup
                    context.dispatch('addMarker', [
                        'historyZoom',
                        location,
                        {
                            color: color[1],
                            radius: 5,
                            popup: {
                                content: Marker.renderHistoryPopup(location),
                                className: 'history-popup'
                            }
                        }
                    ]);
                }

                // Create polyLine for every location point in sessionLocations, we use multiOptionsPolyline,
                // because we can pass different options (color) for every section of the line.
                let line = L.multiOptionsPolyline(latLngs, {
                    weight: 5,
                    lineCap: 'round',
                    lineJoin: 'round',
                    smoothFactor: 1,
                    multiOptions: {
                        optionIdxFn: (lat, lon, idx) => idx,
                        options: lineOptions
                    }
                });

                context.dispatch('addLayerToGroup', [ 'history', line ]);

                // This is a hack, markers layer should be displayed in front of the polyline, but somehow it
                // does not work and markers are hidden behind the polyline, so we bring the polyline to back.
                line.bringToBack();
            }

            // Hider history markers if zoom level is lower than 13
            context.dispatch('toggleHistoryMarkersOnZoom');
        }
    }
};

export default bootstrap(moduleMap);
